import type { ReactNode } from 'react';

export type BaseFormOption<Value extends string | null> = {
  value: Value;
  label: string;
};

export type FormOption<Value extends string | null = string> =
  BaseFormOption<Value> & {
    disabled?: boolean;
    disabledMessage?: ReactNode;
    hint?: ReactNode;
    /**
     * Use this in case the hint is used _somewhere else_ besides the regular usage.
     * E.g. in a `SearchableMultiSelect` the hint is used to be shown as a tooltip
     * for the selected item.
     * Besides those exceptional cases this flag will not have an affect.
     */
    hideHint?: boolean;
    info?: ReactNode;
  };

export type GroupedFormOption<Value extends string | null = string> = {
  label: string;
  disabled?: boolean;
  disabledMessage?: ReactNode;
  options: FormOption<Value>[];
  hint?: ReactNode;
  info?: ReactNode;
};

/**
 * Options are identified by their `value`, so it must be unique across all
 * provided options (including options nested in groups). A duplicate value
 * breaks selection/removal logic (e.g. in `SearchableMultiSelect`) in subtle
 * ways, since it can no longer tell which occurrence of the value an action
 * refers to.
 */
export function assertUniqueOptionValues<Value extends string | null>(
  options: (BaseFormOption<Value> | { options: BaseFormOption<Value>[] })[]
) {
  const flatOptions = options.flatMap((option) =>
    'options' in option ? option.options : [option]
  );

  const values = flatOptions.map((option) => option.value);

  // quick check for non-unique values (O(n))
  if (new Set(values).size === values.length) return;

  // find exact duplicates for error message (O(n^2))
  const duplicateValues = [
    ...new Set(
      values.filter((value, index) => values.indexOf(value) !== index)
    ),
  ];

  const message = `Option values must be unique. Found duplicate value(s): ${duplicateValues.join(', ')}.`;

  if (PRISMA_CONFIG.stage === 'local' || PRISMA_CONFIG.stage === 'storybook') {
    throw new Error(message);
  } else {
    console.warn(message);
  }
}
